import { TFunction } from "i18next";

import { IconEdit01, IconPlus } from "@pentabd/icons";
import { Button, Text } from "@pentabd/ui";

import {
  ContactColumn,
  ProfileInfoCard,
  StatusBadge,
  ExpandedText,
} from "@components";

import { useUser } from "@hooks/useUser";

import { getAccess } from "@helpers/permissionController";

import { EmployeeCommonInfoType } from "@type/HRM/employee-common-info";
import { EmployeeType } from "@type/HRM/employees";
import { NavigateFunction } from "react-router-dom";
import { ROUTES } from "@constants/routes";

const {
  HRM: { EMPLOYEES },
} = ROUTES;

export const TableColumn = (
  t: TFunction<"translation", undefined>,
  onAddNewResponsibilityOpen: (id: number | null) => void,
  navigate: NavigateFunction,
) => {
  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  return [
    {
      id: 1,
      key: "nameBn",
      name: t("EMPLOYEES.TABLE.COLUMN.NAME"),
      render: (_: string, row: EmployeeType) => (
        <ProfileInfoCard employee={row as any} />
      ),
    },
    {
      id: 2,
      key: "employeeCode",
      name: t("EMPLOYEES.TABLE.COLUMN.EMPLOYEE_ID"),
      render: (employeeID: string) => <Text color="info">{employeeID}</Text>,
    },
    {
      id: 3,
      key: "organization",
      name: t("EMPLOYEES.TABLE.COLUMN.OFFICE"),
      render: (organization: string) => (
        <ExpandedText text={organization} maxLength={50} />
      ),
    },
    {
      id: 4,
      key: "email",
      name: t("EMPLOYEES.TABLE.COLUMN.CONTACTS"),
      render: (_: string, row: EmployeeCommonInfoType) => (
        <ContactColumn employee={row} />
      ),
    },
    {
      id: 5,
      key: "flowStatusId",
      name: t("EMPLOYEES.TABLE.COLUMN.SUSPENDED"),
      render: (flowStatusId: number) =>
        flowStatusId === 106 ? (
          <StatusBadge status="Suspended" />
        ) : (
          <Text size="sm" color="subtitle1">
            ...
          </Text>
        ),
    },
    {
      id: 6,
      key: "employeeTypeIdStr",
      name: t("EMPLOYEES.TABLE.COLUMN.EMPLOYEE_TYPE"),
      render: (employeeTypeIdStr: string) => (
        <Text size="sm" color="subtitle1">
          {employeeTypeIdStr || "..."}
        </Text>
      ),
    },
    {
      id: 7,
      key: "extraResponsibility",
      name:
        getAccess(roles, "CAN_ADD_EXTRA_RESPONSIBILITY") &&
        t("EMPLOYEES.TABLE.COLUMN.ROLE"),
      render: (_: string, row: EmployeeType) => {
        return getAccess(roles, "CAN_ADD_EXTRA_RESPONSIBILITY") &&
          (row?.flowStatusId === 106 || !row?.isActive) ? (
          <></>
        ) : (
          <Button
            fill="outline"
            size="xs"
            type="primary"
            htmlType="button"
            onClick={() =>
              onAddNewResponsibilityOpen(row?.id ? Number(row?.id) : null)
            }
          >
            <IconPlus size="16" fill="primary" />
            {t("EMPLOYEES.ADD_EXTRA_RESPONSIBILITY_BUTTON_TEXT")}
          </Button>
        );
      },
    },
    {
      id: 8,
      key: "id",
      name: "",
      render: (id: number) => {
        return (
          <div>
            {getAccess(roles, "EMPLOYEE_UPDATE_PAGE") ? (
              <div
                onClick={() => {
                  navigate(`${EMPLOYEES}/${id}`);
                }}
                className="cursor-pointer"
              >
                <IconEdit01 size={24} fill="subtitle1" />
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];
};
